import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, map, switchMap, tap, finalize } from 'rxjs';
import { Q8Breakpoints } from 'core';
import { Cart, CartRow, UpdateCartProductResponse } from '../interfaces/cart.model';
import { CartDialog } from '../lib/components/cart-dialog/cart-dialog';
import { CartLoadingOverlay } from '../lib/components/cart-loading-overlay/cart-loading-overlay';

@Injectable({ providedIn: 'root' })
export class CartService {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);
  private overlay = inject(Overlay);
  private dialogRef?: MatDialogRef<CartDialog>;

  // Blocca l'interazione con l'app mentre una chiamata al carrello è in corso;
  // il contatore gestisce eventuali chiamate sovrapposte (es. updateProduct -> getActiveCart)
  private pendingRequests = 0;
  private loadingOverlayRef?: OverlayRef;

  // Carrello attivo caricato all'ingresso nella pagina prodotti;
  private readonly _cart = signal<Cart | null>(null);
  readonly cart = this._cart.asReadonly();

  // Carrello di una proposta d'ordine caricato con getCart; null per il carrello attivo del catalogo
  private orderCartId: string | null = null;

  // Il carrello attivo è l'ultimo carrello dell'utente non archiviato né in bozza;
  // se non esiste, il backend ne crea uno nuovo
  getActiveCart(): Observable<Cart> {
    this.orderCartId = null;
    return this.trackLoading(
      this.http.get<Cart>('api/carrelloapp').pipe(
        tap(cart => this._cart.set(cart))
      )
    );
  }

  // Carrello di una proposta d'ordine: righe e svuotamento passano dal suo id
  getCart(idCarrello: string): Observable<Cart> {
    this.orderCartId = idCarrello;
    return this.trackLoading(
      this.http.get<Cart>(`api/carrelloapp/${idCarrello}`).pipe(
        tap(cart => this._cart.set(cart))
      )
    );
  }

  // Minimo da avvisare nell'aggiunta: quello della riga se il prodotto è già nel carrello, che tiene conto anche del listino del cliente
  minimumPrice(idProdotto: string, prezzoMinimoRuolo: number | null | undefined): number | undefined {
    return this.cartRow(idProdotto)?.prezzoMinimo ?? prezzoMinimoRuolo ?? undefined;
  }

  // Riga del prodotto già nel carrello: il dialog di aggiunta riparte dalla sua quantità e dal suo prezzo
  cartRow(idProdotto: string): CartRow | undefined {
    return (this.cart()?.righe ?? []).find(r => !r.archiviata && r.idProdotto === idProdotto);
  }

  // Imposta la quantità esatta della riga (0 = il backend la elimina); il
  // backend crea il carrello se non esiste e, senza prezzo, applica il
  // listino. Poi ricarica il carrello per aggiornare il signal
  updateProduct(idProdotto: string, quantita: number, prezzo?: number): Observable<UpdateCartProductResponse> {
    return this.trackLoading(
      this.http.post<UpdateCartProductResponse>('api/carrelloapp/righe', {
        ...(this.orderCartId ? { idCarrello: this.orderCartId } : {}),
        idProdotto,
        quantita,
        ...(prezzo !== undefined ? { prezzo } : {}),
      }).pipe(
        switchMap(response =>
          this.reloadCart().pipe(
            map(() => response)
          )
        )
      )
    );
  }

  // Svuota il carrello attivo, poi ricarica il carrello per aggiornare il signal;
  // il carrello di una proposta si svuota e resta
  clearCart(): Observable<Cart> {
    return this.trackLoading(
      this.http.delete(`api/carrelloapp${this.orderCartId ? '/' + this.orderCartId : ''}`).pipe(
        switchMap(() => this.reloadCart())
      )
    );
  }

  private reloadCart(): Observable<Cart> {
    return this.orderCartId ? this.getCart(this.orderCartId) : this.getActiveCart();
  }

  // Nella proposta d'ordine il carrello non porta a una nuova proposta
  open(showProposal = true) {
    if (this.dialogRef) {
      return;
    }

    const isMobile = this.breakpointObserver.isMatched(`(max-width: ${Q8Breakpoints.tablet - 1}px)`);

    this.dialogRef = this.dialog.open(CartDialog, {
      position: { top: '0', right: '0' },
      height: '100dvh',
      width: isMobile ? '100vw' : '400px',
      maxWidth: '100vw',
      panelClass: 'cart-panel',
      data: { showProposal },
      // Disabilita l'animazione di default (zoom centrale): lo slide da destra
      // è gestito via CSS sul pannello
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }

  close() {
    this.dialogRef?.close();
  }

  // Mostra un overlay bloccante finché la richiesta è in corso; il contatore
  // evita che chiamate annidate (es. updateProduct -> getActiveCart) nascondano
  // l'overlay prima del completamento della richiesta esterna
  private trackLoading<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      tap({ subscribe: () => this.showLoadingOverlay() }),
      finalize(() => this.hideLoadingOverlay())
    );
  }

  private showLoadingOverlay() {
    this.pendingRequests++;

    if (!this.loadingOverlayRef) {
      this.loadingOverlayRef = this.overlay.create({
        positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
        scrollStrategy: this.overlay.scrollStrategies.block(),
        hasBackdrop: true,
        backdropClass: 'cart-loading-backdrop',
        panelClass: 'cart-loading-panel',
      });
      this.loadingOverlayRef.attach(new ComponentPortal(CartLoadingOverlay));
    }
  }

  private hideLoadingOverlay() {
    this.pendingRequests--;

    if (this.pendingRequests <= 0) {
      this.pendingRequests = 0;
      this.loadingOverlayRef?.dispose();
      this.loadingOverlayRef = undefined;
    }
  }
}
